import React, { useCallback, useEffect, useState } from "react";
import { WorkingTimeClassicType } from "../../__generated__/graphql";
import QuantityField from "../QuantityField";
import { useI18n } from "../../hooks/i18n-hooks";

export type MonthlyPercentClassicComponentProps = {
    workingTime: WorkingTimeClassicType;
    onChange: (v: { workingTime: WorkingTimeClassicType; percentage?: number }) => void;
    disabled?: boolean;
    hoursPerWeek: number;
};

const MonthlyPercentClassicComponent: React.FC<MonthlyPercentClassicComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
    hoursPerWeek,
}) => {
    const i18n = useI18n();
    const buildPercentage = useCallback(
        (unitCount: number) => {
            const valueAsNumberPre = 100 * (unitCount / hoursPerWeek);
            const valueAsNumber = Math.round(valueAsNumberPre * 100) / 100;
            return valueAsNumber;
        },
        [hoursPerWeek],
    );

    const [value, setValue] = useState<number | undefined>(
        buildPercentage(workingTime.unitCount || 0),
    );

    const updateWorkingTime = useCallback(
        (newValue?: number) => {
            setValue(newValue);
            const factor = Number(Number(newValue?.toString().replace(",", ".")).toFixed(3)) / 100;
            if (!isNaN(factor)) {
                const valueAsNumberPre = hoursPerWeek * factor;
                const valueAsNumber = Math.round((valueAsNumberPre + Number.EPSILON) * 100) / 100;
                let changedValue: WorkingTimeClassicType | null;
                if (valueAsNumber <= 0 || valueAsNumber > 24 * 7) {
                    changedValue = { ...workingTime, unitCount: null };
                } else {
                    changedValue = { ...workingTime, unitCount: valueAsNumber };
                }
                onChange({ workingTime: changedValue });
            } else {
                onChange({ workingTime: { ...workingTime, unitCount: null } });
            }
        },
        [hoursPerWeek, onChange, workingTime],
    );

    // Resync local input value AND renormalize the parent's persisted spec
    // when the upstream percentage changes. Kept as an effect (rather than a
    // render-phase "adjust state on prop change" block) so the mount-time
    // normalization — clamping out-of-range `unitCount` and propagating
    // `onChange` upward — still runs on the first render.
    useEffect(() => {
        const newPercentage = buildPercentage(workingTime.unitCount || 0);
        if (value !== newPercentage) {
            updateWorkingTime(newPercentage);
        }
    }, [buildPercentage, updateWorkingTime, value, workingTime.unitCount]);

    return (
        <QuantityField
            style={{ flexShrink: 1, flexGrow: 0, width: "auto" }}
            label={i18n.t("utils.working_time.percent_label")}
            selectTextOnFocus
            error={value === undefined}
            showErrorText={false}
            textAlign="right"
            maxValue={100}
            disabled={disabled}
            value={value}
            right="%"
            onChange={updateWorkingTime}
            keyboardType="decimal-pad"
            returnKeyType="done"
        />
    );
};

export default MonthlyPercentClassicComponent;
